import { AfterViewInit, Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Partner } from '../../partner/models';
import { ConfigurationKey } from '../models/configuration-key';
import { ConfigurationKeyUsage } from '../models/configuration-key-usage';
import { DataField } from '../models/data-field';
import { DataFieldGroup } from '../models/data-field-group';
import { DataFieldType } from '../models/data-field-type';
import { DeveloperService } from '../services/developer.service';
import { PartnerService } from '../../partner/services/partner.service';
import { DistributionObjectSelectorComponent } from '../../distribution/distribution-object-selector.component';
import { FormatType } from '../models/format-type';
import { DocumentConfigSearch, DocumentConfigService } from '../../distribution/services';
import { Direction, DocumentConfigSearchDisplay, DocumentTypeEntity } from '../../distribution/models';

import { PartnerSelectorComponent } from '../../partner/partner-selector.component';
import { DeveloperDataFieldComponent } from '../developer-data-field/developer-data-field.component';

@Component({
    selector: 'app-developer-configuration-key',
    templateUrl: './developer-configuration-key.component.html',
    styleUrls: ['./developer-configuration-key.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, DistributionObjectSelectorComponent, PartnerSelectorComponent, DeveloperDataFieldComponent]
})

export class DeveloperConfigurationKeyComponent implements OnInit, AfterViewInit {
  public formData: UntypedFormGroup;
  public searchObject: any = { splitFromAndTo: false };
  public tooltips: { [key: string]: string };

  partner: Partner;
  selectedDistributionObject: DocumentTypeEntity;
  distributionObjects: DocumentTypeEntity[];
  formatTypes: FormatType[] = [];
  mergedConfigList: DocumentConfigSearchDisplay[] = [];
  documentConfigList: any[] = [];

  @Input() configKey: ConfigurationKey;
  @Input() usages: ConfigurationKeyUsage[];
  @Input() dataFieldGroups: DataFieldGroup[];
  @Input() dataFieldTypes: DataFieldType[];

  @ViewChild('distributionObjectSelector', { static: true }) private distributionObjectSelector: DistributionObjectSelectorComponent;

  constructor(
    public modal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private developerService: DeveloperService,
    private partnerService: PartnerService,
    private documentConfigService: DocumentConfigService) { }

  async ngOnInit(): Promise<void> {
    this.formData = this.buildForm();
    this.developerService.getFormatTypes().then(result => {
      this.formatTypes = result;
    });

    let inConfigList: DocumentConfigSearchDisplay[] = [];
    let outConfigList: DocumentConfigSearchDisplay[] = [];

    await Promise.all([
      this.documentConfigService.getDocumentConfigs(new DocumentConfigSearch(Direction.In)).then(result => { inConfigList = result; }),
      this.documentConfigService.getDocumentConfigs(new DocumentConfigSearch(Direction.Out)).then(result => { outConfigList = result; }),
    ]);

    this.mergedConfigList = inConfigList.concat(outConfigList);
    this.documentConfigList = this.mergedConfigList.sort((a, b) =>
      a.DistributionObjectName.localeCompare(b.DistributionObjectName) ||
      a.Direction.localeCompare(b.Direction) ||
      a.FormatName.localeCompare(b.FormatName) ||
      a.VariantName.localeCompare(b.VariantName) ||
      a.VersionName.localeCompare(b.VersionName));
  }

  ngAfterViewInit(): void {
    this.setDynamicField(this.configKey.DynamicForeignKey);
  }

  getUsageTooltip(usage: string){
    switch(usage){
      case 'Partner_General': return {name: 'Partner Generel', tooltip: 'Will be presented in configuration key tab for all partners'};
      case 'Partner_Specific': return {name: 'Partner Specific', tooltip: 'Will be presented in configuration key tab only for the one partner picked'};
      case 'Distribution_General': return {name: 'Distribution General', tooltip: 'Will always be presented in the beginning of the distribution.'};
      case 'Distribution_DocumentConfig': return {name: 'Distribution Document Configuration', tooltip: 'Will be presented on the distribution but only if the picked Document config is chosen on the in or outbound side.'};
      case 'Distribution_DistributionObject': return {name: 'Distribution Document  type', tooltip: 'Will be presented on the distribution but only if the document type picked is chosen.'};
      case 'Distribution_Partner': return {name: 'Distribution Partner', tooltip: 'Will be presented on the distribution but only if the chosen partner has been picked.'};
      case 'Distribution_FormatType': return {name: 'Distribution Format type', tooltip: 'Will be presented on the distribution but only if the in or outbound document configuration point to the format type picked.'};
      case 'Distribution_Advanced': return {name: 'Distribution Advanced', tooltip: 'Will always be presented on the distribution, but only below the advanced section'};
      default: return {name: '(None)', tooltip: 'Unknown'};
    }
  }

  usageType(usageid?: number): string {
    if (usageid === undefined) {
      usageid = this.formData.get('usageId').value as number;
    }
    const usage = this.usages.find(keyUsage => keyUsage.KeyUsageId === usageid);
    const keyName = usage?.KeyUsageName;

    switch (keyName) {
      case 'Distribution_DistributionObject':
        return 'DistributionObject';
      case 'Distribution_DocumentConfig':
        return 'DocumentConfig';
      case 'Distribution_FormatType':
        return 'FormatType';
      case 'Distribution_Partner':
        return 'Partner';
      case 'Partner_Specific':
        return 'Partner';
    }
    return '';
  }

  buildForm(): UntypedFormGroup {
    return this.fb.group({
      name: [this.configKey.KeyName, Validators.required],
      usageId: [this.configKey.ConfigurationKeyUsagesId, Validators.min(0)],
      formatType: this.usageType(this.configKey.ConfigurationKeyUsagesId) === 'FormatType' ? this.configKey.DynamicForeignKey : null,
      documentConfig: this.usageType(this.configKey.ConfigurationKeyUsagesId) === 'DocumentConfig' ? this.configKey.DynamicForeignKey : null,
      dataField: this.fb.control({
        dataFieldGroupsId: this.configKey.DataField.DataFieldGroupsId,
        dataTypeId: this.configKey.DataField.DataTypesId,
        displayName: this.configKey.DataField.DisplayName,
        isRequired: this.configKey.DataField.IsRequired,
        dataLength: this.configKey.DataField.DataLength,
        regex: this.configKey.DataField.Regex,
        regexText: this.configKey.DataField.RegexErrorMessage,
        infoText: this.configKey.DataField.InfoText,
        defaultValue: this.configKey.DataField.DefaultValue,
        sortOrder: this.configKey.DataField.SortOrder,
        choices: this.configKey.DataField.Choices,
      }),
    });
  }

  async setDynamicField(dynamicKey): Promise<void> {
    const usagetype = this.usageType(this.formData.get('usageId').value as number);
    switch (usagetype) {
      case 'DistributionObject':
        this.distributionObjects = await this.developerService.getDocumentTypes();
        this.selectedDistributionObject = this.distributionObjects.find(doo => doo.Id === dynamicKey);
        if (this.selectedDistributionObject) {
          this.distributionObjectSelector.setSelectedDistributionObjectId(this.selectedDistributionObject.Id);
        }
        break;
      case 'DocumentConfig':
        // dynamicKey = 'DocumentConfig ID';
        break;
      case 'FormatType':
        // dynamicKey = 'FormatType ID';
        break;
      case 'Partner':
        this.partner = await this.partnerService.getPartner(this.configKey.DynamicForeignKey);
        break;
    }
  }

  getDynamicKey(): number {
    const usagetype = this.usageType(this.formData.get('usageId').value as number);
    let dynamicKey = null;
    switch (usagetype) {
      case 'DistributionObject':
        dynamicKey = this.selectedDistributionObject.Id;
        break;
      case 'DocumentConfig':
        dynamicKey = this.formData.get('documentConfig').value as number;
        break;
      case 'FormatType':
        dynamicKey = this.formData.get('formatType').value as number;
        break;
      case 'Partner':
        dynamicKey = this.partner.PartnersId;
        break;
      default:
        dynamicKey = null;
    }
    return dynamicKey;
  }

  ok(): void {
    if (this.formData.valid) {
      const v = this.formData.value;
      const dynamicKey = this.getDynamicKey();

      const df: DataField = {
        DataFieldsId: this.configKey.DataField.DataFieldsId,
        DataTypesId: v.dataField.dataTypeId,
        DisplayName: v.dataField.displayName,
        IsRequired: v.dataField.isRequired,
        DataLength: v.dataField.dataLength,
        DefaultValue: v.dataField.defaultValue,
        Regex: v.dataField.regex,
        RegexErrorMessage: v.dataField.regexText,
        DataFieldGroupsId: v.dataField.dataFieldGroupsId,
        SortOrder: v.dataField.sortOrder,
        InfoText: v.dataField.infoText,
        Choices: v.dataField.choices,
      };
      const ck: ConfigurationKey = {
        ConfigurationKeysId: this.configKey.ConfigurationKeysId,
        KeyName: v.name,
        ConfigurationKeyUsagesId: v.usageId,
        DataField: df,
        DynamicForeignKey: dynamicKey,
        CreatedBy: v.CreatedBy,
        CreatedDate: v.CreatedDate,
        UpdatedBy: v.UpdatedBy,
        UpdatedDate: v.UpdatedDate
      };
      this.modal.close(ck);
    }
  }

  cancel(): void {
    this.modal.dismiss();
  }
}
